import { Modal } from '@/components/common/Modal'
import PDFViewer from '@/components/PDFViewer'
import { getAllOrderReports, getOrderReportById } from '@/features/report/services/reportClient'
import { logger } from '@/sentry.config'
import type { OrderReportResponse } from '@/types/OrderReportTypes'
import type { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import { ChevronDown } from 'flowbite-react-icons/outline'
import React, { useEffect, useMemo, useRef, useState } from 'react'

export type OrderReportDocumentsMenuProps = {
  orderId?: string
  orderData?: OrderFormInfoData
  /** Opens the requisition / order form viewer (same as former “View Order Form”). */
  onOpenOrderForm?: () => void
  /** When true, render a standalone "View Lab Report" pill that opens the latest report PDF directly. */
  showViewLabReportButton?: boolean
  /** When true, use parent-provided list instead of getAllOrderReports. */
  useParentReportList?: boolean
  parentReports?: OrderReportResponse[]
  parentReportsLoading?: boolean
}

type SortKey = {
  generatedAtMs: number | null
  version: number | null
}

const parseSortKey = (report: OrderReportResponse): SortKey => {
  const generatedAtMs = report.generated_at ? new Date(report.generated_at).getTime() : NaN
  const safeGeneratedAtMs = Number.isFinite(generatedAtMs) && generatedAtMs > 0 ? generatedAtMs : null
  const version = report.version ?? null
  return { generatedAtMs: safeGeneratedAtMs, version }
}

const formatReportDate = (dateString?: string | null) => {
  if (!dateString) return 'Unknown date'
  const ms = new Date(dateString).getTime()
  if (!Number.isFinite(ms) || ms <= 0) return 'Unknown date'
  return new Date(ms).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatReportLabel = (report: OrderReportResponse, isLatest: boolean) => {
  const versionLabel = report.version !== null && report.version !== undefined ? `Version ${report.version}` : 'Version'
  const dateLabel = formatReportDate(report.generated_at)
  return `${isLatest ? 'Latest — ' : ''}${versionLabel} • ${dateLabel}`
}

function pdfFilenameForReport(report: OrderReportResponse): string {
  const v =
    report.version !== null && report.version !== undefined ? String(report.version) : 'report'
  return `Lab-Report-v${v}.pdf`
}

/** Pill "Documents" dropdown: order form + report PDF versions (modal viewer). */
export function OrderReportDocumentsMenu({
  orderId,
  orderData,
  onOpenOrderForm,
  showViewLabReportButton = false,
  useParentReportList = false,
  parentReports = [],
  parentReportsLoading = false,
}: OrderReportDocumentsMenuProps) {
  const [reports, setReports] = useState<OrderReportResponse[]>([])
  const [isLoadingReports, setIsLoadingReports] = useState(false)
  const displayReports = useParentReportList ? parentReports : reports
  const displayLoading = useParentReportList ? parentReportsLoading : isLoadingReports
  const [documentsMenuOpen, setDocumentsMenuOpen] = useState(false)
  const [reportPdfModalOpen, setReportPdfModalOpen] = useState(false)
  const [modalReportId, setModalReportId] = useState<string>('')
  const [modalPdfBytes, setModalPdfBytes] = useState<Uint8Array | null>(null)
  const [modalPdfLoading, setModalPdfLoading] = useState(false)
  const [modalPdfError, setModalPdfError] = useState<string | null>(null)
  const documentsDropdownRef = useRef<HTMLDivElement>(null)
  const currentModalPdfRequestRef = useRef<string | null>(null)
  const currentReportsRequestRef = useRef<string | null>(null)

  const callerFirst = orderData?.orderingProvider?.name?.firstName ?? ''
  const callerLast = orderData?.orderingProvider?.name?.lastName ?? ''
  const caller = useMemo(() => {
    const combined = `${callerFirst} ${callerLast}`.trim()
    return combined || 'spa'
  }, [callerFirst, callerLast])

  const sortedReports = useMemo(() => {
    const next = [...displayReports]
    next.sort((a, b) => {
      const aKey = parseSortKey(a)
      const bKey = parseSortKey(b)

      if (aKey.generatedAtMs !== null || bKey.generatedAtMs !== null) {
        const aMs = aKey.generatedAtMs ?? -1
        const bMs = bKey.generatedAtMs ?? -1
        if (aMs !== bMs) return bMs - aMs
      }

      if (aKey.version !== null || bKey.version !== null) {
        const aV = aKey.version ?? -1
        const bV = bKey.version ?? -1
        if (aV !== bV) return bV - aV
      }

      return a.id.localeCompare(b.id)
    })
    return next
  }, [displayReports])

  const latestReportId = useMemo(() => sortedReports[0]?.id ?? '', [sortedReports])
  const hasReports = sortedReports.length > 0

  const modalReport = useMemo(
    () => sortedReports.find((r) => r.id === modalReportId),
    [sortedReports, modalReportId],
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (documentsDropdownRef.current && !documentsDropdownRef.current.contains(event.target as Node)) {
        setDocumentsMenuOpen(false)
      }
    }
    if (documentsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [documentsMenuOpen])

  useEffect(() => {
    if (!reportPdfModalOpen || !orderId || !modalReportId) {
      return
    }

    const requestId = `${orderId}-${modalReportId}-${caller}-modal-pdf`
    currentModalPdfRequestRef.current = requestId

    const fetchModalPdf = async () => {
      try {
        setModalPdfLoading(true)
        setModalPdfError(null)
        setModalPdfBytes(null)

        const pdfBytes = await getOrderReportById(orderId, modalReportId, caller)
        if (currentModalPdfRequestRef.current !== requestId) return

        setModalPdfBytes(pdfBytes)
      } catch (error) {
        if (currentModalPdfRequestRef.current !== requestId) return
        logger.error('Failed to fetch report PDF for viewer modal', {
          orderId,
          reportId: modalReportId,
          error: error instanceof Error ? error.message : String(error),
        })
        setModalPdfError(error instanceof Error ? error.message : 'Failed to fetch report PDF.')
        setModalPdfBytes(null)
      } finally {
        if (currentModalPdfRequestRef.current === requestId) {
          setModalPdfLoading(false)
        }
      }
    }

    fetchModalPdf()

    return () => {
      currentModalPdfRequestRef.current = null
    }
  }, [reportPdfModalOpen, modalReportId, orderId, caller])

  useEffect(() => {
    if (useParentReportList) return

    if (!orderId) {
      setReports([])
      currentReportsRequestRef.current = null
      return
    }

    const requestId = `${orderId}-${caller}`
    currentReportsRequestRef.current = requestId

    const fetchAllReports = async () => {
      try {
        setIsLoadingReports(true)

        const allReports = await getAllOrderReports(orderId, caller)
        if (currentReportsRequestRef.current !== requestId) return

        setReports(allReports)
      } catch (error) {
        if (currentReportsRequestRef.current !== requestId) return
        logger.error('Failed to fetch reports for documents menu', {
          orderId,
          error: error instanceof Error ? error.message : String(error),
        })
        setReports([])
      } finally {
        if (currentReportsRequestRef.current === requestId) {
          setIsLoadingReports(false)
        }
      }
    }

    fetchAllReports()

    return () => {
      currentReportsRequestRef.current = null
    }
  }, [orderId, caller, useParentReportList])

  if (!orderId && !onOpenOrderForm) {
    return null
  }

  if (!hasReports && !displayLoading && !onOpenOrderForm) {
    return null
  }

  const showOrderFormItem = Boolean(onOpenOrderForm)
  const showReportSection = Boolean(orderId) && (displayLoading || hasReports)
  const menuHasMultipleSections = showOrderFormItem && showReportSection
  const buttonDisabled = !onOpenOrderForm && (displayLoading || !hasReports)

  const openLatestReport = () => {
    if (!latestReportId) return
    setDocumentsMenuOpen(false)
    setModalReportId(latestReportId)
    setModalPdfBytes(null)
    setModalPdfError(null)
    setReportPdfModalOpen(true)
  }

  const showViewLabReport = showViewLabReportButton && Boolean(orderId) && (displayLoading || hasReports)

  return (
    <>
      {showViewLabReport ? (
        <button
          type="button"
          onClick={openLatestReport}
          disabled={displayLoading || !hasReports}
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border-base bg-white px-4 py-2 text-sm font-medium text-text-body shadow-xs transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          View Lab Report
        </button>
      ) : null}
      <div className="relative shrink-0" ref={documentsDropdownRef}>
        <button
          type="button"
          id="order-documents-trigger"
          aria-haspopup="menu"
          aria-expanded={documentsMenuOpen}
          aria-controls="order-documents-menu"
          onClick={() => setDocumentsMenuOpen((open) => !open)}
          disabled={buttonDisabled}
          className="inline-flex items-center gap-2 rounded-full border border-border-base bg-white px-4 py-2 text-sm font-medium text-text-body shadow-xs transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <span>Documents</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 transition-transform ${documentsMenuOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
        {documentsMenuOpen ? (
          <div
            id="order-documents-menu"
            role="menu"
            aria-labelledby="order-documents-trigger"
            className="absolute right-0 top-full z-50 mt-2 max-h-64 min-w-[min(100vw-2rem,20rem)] overflow-y-auto rounded-lg border border-border-base bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 sm:min-w-[22rem]"
          >
            {showOrderFormItem ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setDocumentsMenuOpen(false)
                  onOpenOrderForm?.()
                }}
                className="flex w-full px-4 py-2.5 text-left text-sm text-text-body transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Order Form
              </button>
            ) : null}
            {menuHasMultipleSections ? (
              <div
                className="my-1 border-t border-border-base dark:border-gray-700"
                role="separator"
                aria-hidden
              />
            ) : null}
            {displayLoading && orderId ? (
              <div className="px-4 py-2.5 text-sm text-text-body dark:text-gray-400">Loading reports…</div>
            ) : null}
            {sortedReports.map((report) => (
              <button
                key={report.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  setDocumentsMenuOpen(false)
                  setModalReportId(report.id)
                  setModalPdfBytes(null)
                  setModalPdfError(null)
                  setReportPdfModalOpen(true)
                }}
                className="flex w-full px-4 py-2.5 text-left text-sm text-text-body transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {formatReportLabel(report, report.id === latestReportId)}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <Modal
        isOpen={reportPdfModalOpen}
        onClose={() => setReportPdfModalOpen(false)}
        title="Report viewer"
        size="xl"
      >
        {modalReport ? (
          <p className="mb-3 text-sm text-text-body dark:text-gray-300">
            {formatReportLabel(modalReport, modalReport.id === latestReportId)}
          </p>
        ) : null}
        <div className="min-h-[50vh] rounded-base border border-border-base bg-white dark:border-gray-700 dark:bg-gray-900/60">
          {modalPdfLoading ? (
            <div className="flex items-center justify-center p-10 text-sm text-text-body dark:text-gray-200">
              Loading report PDF…
            </div>
          ) : modalPdfError ? (
            <div className="p-6 text-sm text-red-800 dark:text-red-200">{modalPdfError}</div>
          ) : modalPdfBytes && modalReport ? (
            <PDFViewer
              pdfBytes={modalPdfBytes}
              filename={pdfFilenameForReport(modalReport)}
              className="max-h-[min(70vh,800px)] min-h-[40vh]"
            />
          ) : (
            <div className="p-6 text-sm text-text-body dark:text-gray-200">No PDF available.</div>
          )}
        </div>
      </Modal>
    </>
  )
}
